import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Check } from 'lucide-react';
import { useTasks, useProjects, useAppStore, PRIORITY_COLORS } from '@flowfocus/core';
import type { Task, Project } from '@flowfocus/core';
import { Segmented, Sheet } from '../components/ui';
import { TaskDetail } from '../components/TaskDetail';

// ─── Date helpers (local time) ──────────────────────────────────────────────
const WEEKDAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const WEEKDAYS_LONG = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
// Monday-first index: Mon=0 … Sun=6
const mondayIndex = (d: Date): number => (d.getDay() + 6) % 7;
const addDays = (d: Date, n: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const addMonths = (d: Date, n: number): Date => {
  const x = new Date(d.getFullYear(), d.getMonth() + n, 1);
  return x;
};
const startOfWeek = (d: Date): Date => addDays(startOfDay(d), -mondayIndex(d));

const eventColor = (task: Task, projects: Project[]): string =>
  projects.find((p) => p.id === task.projectId)?.color ?? PRIORITY_COLORS[task.priority];

const longDateLabel = (d: Date): string =>
  `${WEEKDAYS_LONG[d.getDay()]}, ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;

// ─── Page ───────────────────────────────────────────────────────────────────
export function CalendarPage({ uid }: { uid: string }) {
  const { tasks, addTask, updateTask, deleteTask, toggleComplete } = useTasks(uid);
  const { projects } = useProjects(uid);
  useAppStore(); // keep store subscription consistent with sibling pages

  const [mode, setMode] = useState<'mes' | 'semana'>('mes');
  const [cursor, setCursor] = useState<Date>(() => startOfDay(new Date()));
  const [sheetDay, setSheetDay] = useState<Date | null>(null);
  const [detailTask, setDetailTask] = useState<Task | null>(null);

  const today = useMemo(() => startOfDay(new Date()), []);

  // Map of "YYYY-M-D" -> tasks with a dueDate on that day
  const tasksByDay = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const t of tasks) {
      if (t.dueDate == null) continue;
      const d = new Date(t.dueDate);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const arr = map.get(key);
      if (arr) arr.push(t);
      else map.set(key, [t]);
    }
    return map;
  }, [tasks]);

  const dayTasks = (d: Date): Task[] => {
    const arr = tasksByDay.get(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`) ?? [];
    return [...arr].sort((a, b) => Number(a.completed) - Number(b.completed) || a.order - b.order);
  };

  const noDateCount = useMemo(() => tasks.filter((t) => t.dueDate == null && !t.completed).length, [tasks]);

  // Always-fresh reference for the open task detail (so edits reflect immediately)
  const liveDetail = detailTask ? tasks.find((t) => t.id === detailTask.id) ?? null : null;

  // Month grid: 6 weeks of cells starting on the Monday on/before the 1st
  const monthCells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [cursor]);

  const weekCells = useMemo(() => {
    const ws = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  }, [cursor]);

  const goPrev = () => setCursor((c) => (mode === 'mes' ? addMonths(c, -1) : addDays(c, -7)));
  const goNext = () => setCursor((c) => (mode === 'mes' ? addMonths(c, 1) : addDays(c, 7)));
  const goToday = () => setCursor(startOfDay(new Date()));

  const titleLabel =
    mode === 'mes'
      ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
      : (() => {
          const ws = startOfWeek(cursor);
          const we = addDays(ws, 6);
          if (ws.getMonth() === we.getMonth()) return `${MONTHS[ws.getMonth()]} ${ws.getFullYear()}`;
          return `${MONTHS_SHORT[ws.getMonth()]} – ${MONTHS_SHORT[we.getMonth()]} ${we.getFullYear()}`;
        })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ── Header ── */}
      <div style={{ padding: '22px 22px 12px', flexShrink: 0, borderBottom: '0.5px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-pri)', margin: 0, letterSpacing: '-0.6px', textTransform: 'capitalize' }}>
            {titleLabel}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn-ghost" onClick={goToday}>Hoy</button>
            <div style={{ display: 'flex', gap: 2 }}>
              <button className="icon-btn" onClick={goPrev} title="Anterior"><ChevronLeft size={18} /></button>
              <button className="icon-btn" onClick={goNext} title="Siguiente"><ChevronRight size={18} /></button>
            </div>
            <Segmented<'mes' | 'semana'>
              value={mode}
              options={[{ id: 'mes', label: 'Mes' }, { id: 'semana', label: 'Semana' }]}
              onChange={setMode}
            />
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {mode === 'mes' ? (
          <MonthView
            cells={monthCells}
            month={cursor.getMonth()}
            today={today}
            projects={projects}
            dayTasks={dayTasks}
            onDayClick={(d) => setSheetDay(startOfDay(d))}
            onTaskClick={(t) => setDetailTask(t)}
          />
        ) : (
          <WeekView
            cells={weekCells}
            today={today}
            projects={projects}
            dayTasks={dayTasks}
            onToggle={toggleComplete}
            onDayClick={(d) => setSheetDay(startOfDay(d))}
            onTaskClick={(t) => setDetailTask(t)}
          />
        )}

        {noDateCount > 0 && (
          <div style={{ padding: '14px 22px 40px', color: 'var(--text-mut)', fontSize: 12 }}>
            {noDateCount} {noDateCount === 1 ? 'tarea sin fecha' : 'tareas sin fecha'}
          </div>
        )}
      </div>

      {/* ── Day sheet ── */}
      {sheetDay && (
        <DaySheet
          day={sheetDay}
          tasks={dayTasks(sheetDay)}
          projects={projects}
          onClose={() => setSheetDay(null)}
          onToggle={toggleComplete}
          onTaskClick={(t) => { setDetailTask(t); setSheetDay(null); }}
          onAdd={(name) =>
            addTask({
              name,
              priority: 'none',
              tags: [],
              dueDate: sheetDay.getTime(),
              recurrence: 'none',
              estimatedPomodoros: 1,
              completedPomodoros: 0,
              subtasks: [],
              completed: false,
            })
          }
        />
      )}

      {/* ── Task detail ── */}
      {liveDetail && (
        <TaskDetail
          task={liveDetail}
          projects={projects}
          onClose={() => setDetailTask(null)}
          onUpdate={(c) => updateTask(liveDetail.id, c)}
          onDelete={() => deleteTask(liveDetail.id)}
        />
      )}
    </div>
  );
}

// ─── Month view ─────────────────────────────────────────────────────────────
function MonthView({
  cells, month, today, projects, dayTasks, onDayClick, onTaskClick,
}: {
  cells: Date[];
  month: number;
  today: Date;
  projects: Project[];
  dayTasks: (d: Date) => Task[];
  onDayClick: (d: Date) => void;
  onTaskClick: (t: Task) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      {/* Weekday header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '0.5px solid var(--border)' }}>
        {WEEKDAYS.map((w) => (
          <div key={w} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-mut)', padding: '8px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {w}
          </div>
        ))}
      </div>

      {/* Cells */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(6, 1fr)' }}>
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === month;
          const isToday = sameDay(d, today);
          const list = dayTasks(d);
          const shown = list.slice(0, 3);
          const extra = list.length - shown.length;
          return (
            <div
              key={i}
              onClick={() => onDayClick(d)}
              style={{
                borderRight: (i % 7 !== 6) ? '0.5px solid var(--border)' : 'none',
                borderBottom: '0.5px solid var(--border)',
                minHeight: 92,
                padding: '5px 4px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                background: inMonth ? 'transparent' : 'var(--bg-app)',
                overflow: 'hidden',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: isToday ? 700 : 500,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isToday ? 'var(--primary)' : 'transparent',
                    color: isToday ? 'var(--on-primary)' : inMonth ? 'var(--text-pri)' : 'var(--text-mut)',
                  }}
                >
                  {d.getDate()}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {shown.map((t) => (
                  <EventPill key={t.id} task={t} color={eventColor(t, projects)} onClick={(e) => { e.stopPropagation(); onTaskClick(t); }} />
                ))}
                {extra > 0 && (
                  <span style={{ fontSize: 10.5, color: 'var(--text-mut)', paddingLeft: 4, fontWeight: 600 }}>+{extra}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventPill({ task, color, onClick }: { task: Task; color: string; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        width: '100%',
        textAlign: 'left',
        border: 'none',
        cursor: 'pointer',
        background: task.completed ? 'transparent' : `${color}22`,
        borderRadius: 5,
        padding: '1px 4px',
        overflow: 'hidden',
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0, opacity: task.completed ? 0.5 : 1 }} />
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: task.completed ? 'var(--text-mut)' : 'var(--text-pri)',
          textDecoration: task.completed ? 'line-through' : 'none',
        }}
      >
        {task.name}
      </span>
    </button>
  );
}

// ─── Week view ──────────────────────────────────────────────────────────────
function WeekView({
  cells, today, projects, dayTasks, onToggle, onDayClick, onTaskClick,
}: {
  cells: Date[];
  today: Date;
  projects: Project[];
  dayTasks: (d: Date) => Task[];
  onToggle: (id: string) => void;
  onDayClick: (d: Date) => void;
  onTaskClick: (t: Task) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 0,
      }}
    >
      {cells.map((d, i) => {
        const isToday = sameDay(d, today);
        const list = dayTasks(d);
        return (
          <div key={i} style={{ borderRight: '0.5px solid var(--border)', borderBottom: '0.5px solid var(--border)', minHeight: 160, display: 'flex', flexDirection: 'column' }}>
            <button
              onClick={() => onDayClick(d)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: '10px 12px 8px',
                borderBottom: '0.5px solid var(--border)',
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-mut)', textTransform: 'uppercase' }}>
                {WEEKDAYS[i]}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: isToday ? 700 : 600,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isToday ? 'var(--primary)' : 'transparent',
                  color: isToday ? 'var(--on-primary)' : 'var(--text-pri)',
                }}
              >
                {d.getDate()}
              </span>
            </button>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '6px 8px', gap: 3 }}>
              {list.length === 0 && (
                <span style={{ fontSize: 11, color: 'var(--text-mut)', padding: '4px 4px' }}>—</span>
              )}
              {list.map((t) => (
                <WeekRow
                  key={t.id}
                  task={t}
                  color={eventColor(t, projects)}
                  onToggle={() => onToggle(t.id)}
                  onClick={() => onTaskClick(t)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WeekRow({ task, color, onToggle, onClick }: { task: Task; color: string; onToggle: () => void; onClick: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px', borderRadius: 8 }}>
      <button
        onClick={onToggle}
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          flexShrink: 0,
          cursor: 'pointer',
          border: `1.5px solid ${task.completed ? 'var(--green)' : 'var(--text-mut)'}`,
          background: task.completed ? 'var(--green)' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {task.completed && <Check size={11} color="#fff" strokeWidth={3} />}
      </button>
      <button
        onClick={onClick}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          overflow: 'hidden',
          padding: 0,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span
          style={{
            fontSize: 13,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: task.completed ? 'var(--text-mut)' : 'var(--text-pri)',
            textDecoration: task.completed ? 'line-through' : 'none',
          }}
        >
          {task.name}
        </span>
      </button>
    </div>
  );
}

// ─── Day sheet ──────────────────────────────────────────────────────────────
function DaySheet({
  day, tasks, projects, onClose, onToggle, onTaskClick, onAdd,
}: {
  day: Date;
  tasks: Task[];
  projects: Project[];
  onClose: () => void;
  onToggle: (id: string) => void;
  onTaskClick: (t: Task) => void;
  onAdd: (name: string) => void;
}) {
  const [draft, setDraft] = useState('');
  const submit = () => {
    const name = draft.trim();
    if (!name) return;
    onAdd(name);
    setDraft('');
  };
  return (
    <Sheet title={longDateLabel(day)} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 12 }}>
        {tasks.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--text-mut)', margin: '4px 2px 8px' }}>Sin tareas este día</p>
        )}
        {tasks.map((t) => {
          const color = eventColor(t, projects);
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 2px' }}>
              <button
                onClick={() => onToggle(t.id)}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  flexShrink: 0,
                  cursor: 'pointer',
                  border: `1.5px solid ${t.completed ? 'var(--green)' : 'var(--text-mut)'}`,
                  background: t.completed ? 'var(--green)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {t.completed && <Check size={12} color="#fff" strokeWidth={3} />}
              </button>
              <button
                onClick={() => onTaskClick(t)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  overflow: 'hidden',
                  padding: 0,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: t.completed ? 'var(--text-mut)' : 'var(--text-pri)',
                    textDecoration: t.completed ? 'line-through' : 'none',
                  }}
                >
                  {t.name}
                </span>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
              </button>
            </div>
          );
        })}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); submit(); }} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="submit" className="icon-btn" style={{ width: 28, height: 28, flexShrink: 0 }}><Plus size={16} /></button>
        <input
          className="input-bare"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Añadir tarea para este día"
        />
      </form>
    </Sheet>
  );
}
